import { useState } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { Download, Palette, Type, Eye, Save } from "lucide-react";
import { toast } from "sonner";

interface BoardSettings {
  clientName: string;
  flightNumber: string;
  arrivalTime: string;
  welcomeMessage: string;
  companyName: string;
  backgroundColor: string;
  textColor: string;
  accentColor: string;
  fontSize: string;
  fontFamily: string;
  template: string;
  logoUrl: string;
}

const defaultSettings: BoardSettings = {
  clientName: "أحمد محمد",
  flightNumber: "SV123",
  arrivalTime: "14:30",
  welcomeMessage: "أهلاً وسهلاً",
  companyName: "شركة السياحة الرائدة",
  backgroundColor: "#ffffff",
  textColor: "#1a1a1a",
  accentColor: "#3b82f6",
  fontSize: "large",
  fontFamily: "Tajawal",
  template: "modern",
  logoUrl: ""
};

const templates = [
  { id: "modern", name: "عصري", preview: "bg-gradient-to-br from-blue-50 to-indigo-100" },
  { id: "elegant", name: "أنيق", preview: "bg-gradient-to-br from-gray-50 to-slate-100" },
  { id: "colorful", name: "ملون", preview: "bg-gradient-to-br from-purple-50 to-pink-100" },
  { id: "professional", name: "مهني", preview: "bg-gradient-to-br from-green-50 to-emerald-100" },
  { id: "luxury", name: "راقي", preview: "bg-gradient-to-br from-amber-50 to-yellow-100" }
];

const fontSizes = [
  { value: "small", label: "صغير", size: "text-lg" },
  { value: "medium", label: "متوسط", size: "text-xl" },
  { value: "large", label: "كبير", size: "text-2xl" },
  { value: "xlarge", label: "كبير جداً", size: "text-3xl" }
];

const fontFamilies = [
  { value: "Tajawal", label: "تجوال" },
  { value: "Cairo", label: "القاهرة" },
  { value: "Amiri", label: "أميري" },
  { value: "Noto Sans Arabic", label: "نوتو سانس" }
];

export default function CustomerReceptionBoards() {
  const [settings, setSettings] = useState<BoardSettings>(defaultSettings);
  const [isPreviewMode, setIsPreviewMode] = useState(false);

  const updateSetting = (key: keyof BoardSettings, value: string) => {
    setSettings(prev => ({ ...prev, [key]: value }));
  };

  const generatePDF = () => {
    const printContent = document.getElementById('reception-board');
    if (!printContent) return;

    const printWindow = window.open('', '_blank');
    if (!printWindow) return;

    printWindow.document.write(`
      <!DOCTYPE html>
      <html dir="rtl">
        <head>
          <meta charset="utf-8">
          <title>لوحة استقبال - ${settings.clientName}</title>
          <script src="https://cdn.tailwindcss.com"></script>
          <link href="https://fonts.googleapis.com/css2?family=Tajawal:wght@300;400;500;700&family=Cairo:wght@300;400;500;700&family=Amiri:wght@400;700&family=Noto+Sans+Arabic:wght@300;400;500;700&display=swap" rel="stylesheet">
          <style>
            @page { size: A4 landscape; margin: 0; }
            body { font-family: '${settings.fontFamily}', sans-serif; margin: 0; padding: 20px; }
            .print-board { 
              width: 100%; 
              height: calc(100vh - 40px); 
              display: flex; 
              flex-direction: column; 
              justify-content: center; 
              align-items: center;
              background: ${settings.backgroundColor};
              color: ${settings.textColor};
              text-align: center;
              border-radius: 12px;
              box-shadow: 0 4px 12px rgba(0,0,0,0.1);
            }
          </style>
        </head>
        <body>
          ${printContent.outerHTML}
        </body>
      </html>
    `);

    printWindow.document.close();
    setTimeout(() => {
      printWindow.print();
      printWindow.close();
    }, 500);

    toast.success("تم إنشاء ملف PDF للطباعة");
  };

  const saveTemplate = () => {
    localStorage.setItem('receptionBoardSettings', JSON.stringify(settings));
    toast.success("تم حفظ الإعدادات بنجاح");
  };

  const getTemplateStyles = () => {
    const baseStyles = {
      backgroundColor: settings.backgroundColor,
      color: settings.textColor,
      fontFamily: settings.fontFamily
    };

    switch (settings.template) {
      case 'modern':
        return {
          ...baseStyles,
          background: `linear-gradient(135deg, ${settings.backgroundColor}, ${settings.accentColor}15)`
        };
      case 'elegant':
        return {
          ...baseStyles,
          background: `linear-gradient(to bottom, ${settings.backgroundColor}, ${settings.backgroundColor}f0)`
        };
      case 'colorful':
        return {
          ...baseStyles,
          background: `linear-gradient(45deg, ${settings.accentColor}20, ${settings.backgroundColor}, ${settings.accentColor}10)`
        };
      case 'professional':
        return {
          ...baseStyles,
          background: settings.backgroundColor,
          border: `3px solid ${settings.accentColor}`
        };
      case 'luxury':
        return {
          ...baseStyles,
          background: `radial-gradient(circle at center, ${settings.backgroundColor}, ${settings.accentColor}08)`
        };
      default:
        return baseStyles;
    }
  };

  const getFontSizeClass = () => {
    const sizeMap = {
      'small': 'text-lg',
      'medium': 'text-xl', 
      'large': 'text-2xl',
      'xlarge': 'text-3xl'
    };
    return sizeMap[settings.fontSize as keyof typeof sizeMap] || 'text-xl';
  };

  if (isPreviewMode) {
    return (
      <div className="min-h-screen bg-gray-100 p-4">
        <div className="max-w-6xl mx-auto">
          <div className="flex justify-between items-center mb-4">
            <h1 className="text-2xl font-bold">معاينة لوحة الاستقبال</h1>
            <div className="flex gap-2">
              <Button onClick={generatePDF} className="flex items-center gap-2">
                <Download className="h-4 w-4" />
                طباعة PDF
              </Button>
              <Button variant="outline" onClick={() => setIsPreviewMode(false)}>
                <Eye className="h-4 w-4" />
                العودة للتحرير
              </Button>
            </div>
          </div>
          
          <div 
            id="reception-board"
            className="w-full h-96 flex flex-col justify-center items-center rounded-lg shadow-lg print-board"
            style={getTemplateStyles()}
          >
            {settings.logoUrl && (
              <img src={settings.logoUrl} alt="Logo" className="h-16 mb-4" />
            )}
            
            <div className={`${getFontSizeClass()} font-bold mb-2`} style={{ color: settings.accentColor }}>
              {settings.welcomeMessage}
            </div>
            
            <div className={`${getFontSizeClass()} font-bold mb-4`}>
              {settings.clientName}
            </div>
            
            <div className="text-lg mb-2">
              رقم الرحلة: <span className="font-bold" style={{ color: settings.accentColor }}>{settings.flightNumber}</span>
            </div>
            
            <div className="text-lg mb-4">
              وقت الوصول: <span className="font-bold">{settings.arrivalTime}</span>
            </div>
            
            <div className="text-base text-center opacity-80">
              {settings.companyName}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full max-w-full overflow-x-hidden" dir="rtl">
      <div className="container mx-auto p-6 space-y-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Palette className="h-8 w-8" />
            <h1 className="text-3xl font-bold">لوحات استقبال العملاء</h1>
          </div>
          <div className="flex gap-2">
            <Button onClick={saveTemplate} variant="outline" className="flex items-center gap-2">
              <Save className="h-4 w-4" />
              حفظ الإعدادات
            </Button>
            <Button onClick={() => setIsPreviewMode(true)} className="flex items-center gap-2">
              <Eye className="h-4 w-4" />
              معاينة
            </Button>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* إعدادات التخصيص */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Palette className="h-5 w-5" />
                إعدادات التخصيص
              </CardTitle>
              <CardDescription>
                خصص شكل ومحتوى لوحة الاستقبال
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Tabs defaultValue="content" className="space-y-4">
                <TabsList className="grid w-full grid-cols-3">
                  <TabsTrigger value="content">المحتوى</TabsTrigger>
                  <TabsTrigger value="design">التصميم</TabsTrigger>
                  <TabsTrigger value="template">القوالب</TabsTrigger>
                </TabsList>

                <TabsContent value="content" className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="clientName">اسم العميل</Label>
                      <Input
                        id="clientName"
                        value={settings.clientName}
                        onChange={(e) => updateSetting('clientName', e.target.value)}
                        placeholder="أدخل اسم العميل"
                      />
                    </div>
                    <div>
                      <Label htmlFor="flightNumber">رقم الرحلة</Label>
                      <Input
                        id="flightNumber"
                        value={settings.flightNumber}
                        onChange={(e) => updateSetting('flightNumber', e.target.value)}
                        placeholder="مثال: SV123"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="arrivalTime">وقت الوصول</Label>
                      <Input
                        id="arrivalTime"
                        value={settings.arrivalTime}
                        onChange={(e) => updateSetting('arrivalTime', e.target.value)}
                        placeholder="مثال: 14:30"
                      />
                    </div>
                    <div>
                      <Label htmlFor="welcomeMessage">رسالة الترحيب</Label>
                      <Input
                        id="welcomeMessage"
                        value={settings.welcomeMessage}
                        onChange={(e) => updateSetting('welcomeMessage', e.target.value)}
                        placeholder="أهلاً وسهلاً"
                      />
                    </div>
                  </div>

                  <div>
                    <Label htmlFor="companyName">اسم الشركة</Label>
                    <Input
                      id="companyName"
                      value={settings.companyName}
                      onChange={(e) => updateSetting('companyName', e.target.value)}
                      placeholder="اسم شركتك"
                    />
                  </div>

                  <div>
                    <Label htmlFor="logoUrl">رابط الشعار</Label>
                    <Input
                      id="logoUrl"
                      value={settings.logoUrl}
                      onChange={(e) => updateSetting('logoUrl', e.target.value)}
                      placeholder="https://example.com/logo.png"
                    />
                  </div>
                </TabsContent>

                <TabsContent value="design" className="space-y-4">
                  <div className="grid grid-cols-3 gap-4">
                    <div>
                      <Label htmlFor="backgroundColor">لون الخلفية</Label>
                      <Input
                        id="backgroundColor"
                        type="color"
                        value={settings.backgroundColor}
                        onChange={(e) => updateSetting('backgroundColor', e.target.value)}
                        className="h-10"
                      />
                    </div>
                    <div>
                      <Label htmlFor="textColor">لون النص</Label>
                      <Input
                        id="textColor"
                        type="color"
                        value={settings.textColor}
                        onChange={(e) => updateSetting('textColor', e.target.value)}
                        className="h-10"
                      />
                    </div>
                    <div>
                      <Label htmlFor="accentColor">اللون المميز</Label>
                      <Input
                        id="accentColor"
                        type="color"
                        value={settings.accentColor}
                        onChange={(e) => updateSetting('accentColor', e.target.value)}
                        className="h-10"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="fontSize">حجم الخط</Label>
                      <Select value={settings.fontSize} onValueChange={(value) => updateSetting('fontSize', value)}>
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {fontSizes.map((size) => (
                            <SelectItem key={size.value} value={size.value}>
                              {size.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label htmlFor="fontFamily">نوع الخط</Label>
                      <Select value={settings.fontFamily} onValueChange={(value) => updateSetting('fontFamily', value)}>
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {fontFamilies.map((font) => (
                            <SelectItem key={font.value} value={font.value}>
                              {font.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                </TabsContent>

                <TabsContent value="template" className="space-y-4">
                  <div className="grid grid-cols-1 gap-3">
                    {templates.map((template) => (
                      <div
                        key={template.id}
                        className={`p-4 rounded-lg border-2 cursor-pointer transition-all ${
                          settings.template === template.id 
                            ? 'border-primary bg-primary/5' 
                            : 'border-border hover:border-primary/50'
                        }`}
                        onClick={() => updateSetting('template', template.id)}
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <div className={`w-8 h-8 rounded ${template.preview}`}></div>
                            <span className="font-medium">{template.name}</span>
                          </div>
                          {settings.template === template.id && (
                            <Badge variant="default">محدد</Badge>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </TabsContent>
              </Tabs>
            </CardContent>
          </Card>

          {/* معاينة مصغرة */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Eye className="h-5 w-5" />
                معاينة مصغرة
              </CardTitle>
              <CardDescription>
                شاهد كيف ستبدو لوحة الاستقبال
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div 
                className="w-full h-64 flex flex-col justify-center items-center rounded-lg shadow-md text-center"
                style={getTemplateStyles()}
              >
                {settings.logoUrl && (
                  <img src={settings.logoUrl} alt="Logo" className="h-8 mb-2" />
                )}
                
                <div className="text-lg font-bold mb-1" style={{ color: settings.accentColor }}>
                  {settings.welcomeMessage}
                </div>
                
                <div className="text-lg font-bold mb-2">
                  {settings.clientName}
                </div>
                
                <div className="text-sm mb-1">
                  رقم الرحلة: <span className="font-bold" style={{ color: settings.accentColor }}>{settings.flightNumber}</span>
                </div>
                
                <div className="text-sm mb-2">
                  وقت الوصول: <span className="font-bold">{settings.arrivalTime}</span>
                </div>
                
                <div className="text-xs opacity-70">
                  {settings.companyName}
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* معلومات إضافية */}
        <Card>
          <CardHeader>
            <CardTitle>نصائح لتصميم لوحات الاستقبال</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
              <div>
                <h4 className="font-semibold mb-2">💡 نصائح التصميم:</h4>
                <ul className="space-y-1 text-muted-foreground">
                  <li>• استخدم ألوان متباينة لضمان وضوح النص</li>
                  <li>• اختر خطوط واضحة وحجم مناسب للقراءة من بعيد</li>
                  <li>• أضف شعار الشركة لإضفاء الطابع المهني</li>
                  <li>• تأكد من صحة المعلومات قبل الطباعة</li>
                </ul>
              </div>
              <div>
                <h4 className="font-semibold mb-2">🎨 اختيار القوالب:</h4>
                <ul className="space-y-1 text-muted-foreground">
                  <li>• <strong>عصري:</strong> مناسب للشركات التقنية</li>
                  <li>• <strong>أنيق:</strong> مثالي للشركات الراقية</li>
                  <li>• <strong>ملون:</strong> جذاب للرحلات السياحية</li>
                  <li>• <strong>مهني:</strong> مناسب للشركات التجارية</li>
                  <li>• <strong>راقي:</strong> للخدمات المميزة والـ VIP</li>
                </ul>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}